"use client";

import { Field, Form } from "react-final-form";
import { PencilIcon, X } from "lucide-react";

import { PunchupButton } from "@punchup/ui-common/components/common";
import { cn } from "@punchup/utils/cn";
import { z } from "zod";

const validationSchema = z.object({
  description: z
    .string()
    .max(500, "Description must be 500 characters or less")
    .nullish()
    .transform((val) => val || null),
});

type VenueDescriptionFormValues = z.infer<typeof validationSchema>;

const validate = (values: VenueDescriptionFormValues) => {
  try {
    validationSchema.parse(values);
    return {};
  } catch (e) {
    if (e instanceof z.ZodError) {
      return e.errors.reduce((acc: Record<string, string>, error) => {
        acc[error.path[0]] = error.message;
        return acc;
      }, {});
    }
    return {};
  }
};

interface VenueDescriptionFormProps {
  description: string | null;
  isEditing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSubmit: (values: VenueDescriptionFormValues) => void;
}

const sharedStyles =
  "text-slate-gray-900 p-2 md:p-3 block w-full rounded-lg border border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50";

export const VenueDescriptionForm: React.FC<VenueDescriptionFormProps> = ({
  description,
  isEditing,
  onEdit,
  onCancel,
  onSubmit,
}) => {
  if (!isEditing) {
    return (
      <div>
        <span className="text-base text-secondary-blue whitespace-pre-wrap">
          {description}
        </span>
        <PunchupButton
          onClick={onEdit}
          className="flex items-center gap-2 w-fit mt-4"
        >
          <PencilIcon className="h-4 w-4" /> Edit Description
        </PunchupButton>
      </div>
    );
  }

  return (
    <Form
      onSubmit={onSubmit}
      validate={validate}
      initialValues={{ description }}
      render={({ handleSubmit, pristine, form, hasValidationErrors }) => (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Field name="description">
            {({ input, meta }) => (
              <div>
                <textarea
                  {...input}
                  className={cn(sharedStyles, "min-h-[180px] resize-none")}
                  placeholder="Enter venue description"
                />
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-slate-500">
                    {input.value?.length || 0}/500 characters
                  </span>
                </div>
                {meta.error && (meta.touched || meta.dirty) && (
                  <span className="text-red-500 text-sm">{meta.error}</span>
                )}
                {(meta.touched || meta.dirty) && input.value === "" && (
                  <span className="text-red-500 text-sm">
                    A description is not required but recommended. Are you sure
                    you want to proceed without a description?
                  </span>
                )}
              </div>
            )}
          </Field>

          <div className="flex items-center gap-x-4">
            <PunchupButton
              type="button"
              variant="plain"
              onClick={() => {
                form.reset();
                onCancel();
              }}
            >
              Cancel
            </PunchupButton>
            <PunchupButton
              type="submit"
              disabled={pristine || hasValidationErrors}
            >
              Save
            </PunchupButton>
          </div>
        </form>
      )}
    />
  );
};
